/**
 * Recursos do lançamento (docs/ESCOPO-LANCAMENTO.md):
 * aceite de termos, menores de idade, setores restritos de patrocinador
 * e Central de Ocorrências (Triagem).
 */

// ============================================================================
// TERMOS E PRIVACIDADE
// ============================================================================

/** Versão vigente dos Termos de Uso (data de publicação, YYYY-MM-DD) */
export const TERMS_VERSION = '2026-09-29';
/** Versão vigente da Política de Privacidade (data de publicação, YYYY-MM-DD) */
export const PRIVACY_VERSION = '2026-09-29';

/** Registro do aceite gravado no documento do usuário */
export interface LegalAcceptance {
  termsVersion: string;
  privacyVersion: string;
  /** ISO 8601 UTC */
  acceptedAt: string;
  ip: string | null;
}

// ============================================================================
// MENORES DE IDADE (LGPD art. 14 / ECA)
// ============================================================================

export const ADULT_AGE = 18;

export const GUARDIAN_RELATIONSHIPS = ['mae', 'pai', 'tutor', 'responsavel_legal'] as const;
export type GuardianRelationship = typeof GUARDIAN_RELATIONSHIPS[number];

export interface GuardianInfo {
  name: string;
  /**
   * CPF do responsável. No banco: cifrado (AES-256-GCM, formato v1 do
   * services/crypto.ts). Nunca exposto nas respostas, exceto na exportação
   * LGPD do próprio titular.
   */
  cpf?: string;
  /** Últimos 4 dígitos do CPF do responsável (exibição) */
  cpfLast4?: string;
  /** HMAC do CPF do responsável (comparação sem descriptografar) */
  cpfHash?: string;
  email: string;
  relationship: GuardianRelationship;
  /** ISO 8601 UTC do consentimento */
  consentAt: string;
}

/**
 * Calcula a idade completa a partir de uma data YYYY-MM-DD.
 * Retorna null se a data for inválida ou estiver no futuro.
 */
export function calculateAge(birthDate: string, now: Date = new Date()): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  if (date.getTime() > now.getTime()) return null;

  let age = now.getUTCFullYear() - year;
  const hadBirthdayThisYear =
    now.getUTCMonth() + 1 > month || (now.getUTCMonth() + 1 === month && now.getUTCDate() >= day);
  if (!hadBirthdayThisYear) age -= 1;
  return age;
}

export function isMinorByBirthDate(birthDate: string, now: Date = new Date()): boolean {
  const age = calculateAge(birthDate, now);
  return age !== null && age < ADULT_AGE;
}

// ============================================================================
// SETORES DE PATROCINADOR (seção 6 do escopo)
// ============================================================================

/**
 * - blocked: cadastro recusado
 * - adult_only: só pode patrocinar seeker adulto e sem menores envolvidos
 * - compliance_notice: permitido, com aviso de conformidade por conta do patrocinador
 * - allowed: sem restrição
 */
export type SponsorSectorRule = 'blocked' | 'adult_only' | 'compliance_notice' | 'allowed';

export interface SponsorSectorDefinition {
  label: string;
  rule: SponsorSectorRule;
  /** Mensagem exibida ao usuário quando o setor é bloqueado ou exige aviso */
  message?: string;
}

const COMPLIANCE_MESSAGE =
  'Setor regulado: a conformidade da publicidade com a legislação e as normas do CONAR é responsabilidade do patrocinador.';

export const SPONSOR_SECTORS = {
  apostas: {
    label: 'Apostas / bets',
    rule: 'blocked',
    message: 'Empresas de apostas não podem patrocinar pela PatrociConnect.',
  },
  tabaco: {
    label: 'Tabaco / cigarro eletrônico',
    rule: 'blocked',
    message: 'Publicidade de tabaco e cigarro eletrônico é proibida em eventos esportivos.',
  },
  medicamentos: {
    label: 'Medicamentos',
    rule: 'blocked',
    message: 'O setor de medicamentos não é aceito como patrocinador na PatrociConnect.',
  },
  armas: {
    label: 'Armas e munições',
    rule: 'blocked',
    message: 'O setor de armas e munições não é aceito como patrocinador na PatrociConnect.',
  },
  conteudo_adulto: {
    label: 'Conteúdo adulto',
    rule: 'blocked',
    message: 'Empresas de conteúdo adulto não são aceitas como patrocinadoras na PatrociConnect.',
  },
  bebidas_alcoolicas: {
    label: 'Bebidas alcoólicas',
    rule: 'adult_only',
    message:
      'Bebidas alcoólicas só podem patrocinar maiores de 18 anos e eventos sem menores, seguindo as regras do CONAR.',
  },
  financeiro: { label: 'Serviços financeiros', rule: 'compliance_notice', message: COMPLIANCE_MESSAGE },
  suplementos: { label: 'Suplementos alimentares', rule: 'compliance_notice', message: COMPLIANCE_MESSAGE },
  alimentos_bebidas: { label: 'Alimentos e bebidas não alcoólicas', rule: 'allowed' },
  varejo: { label: 'Varejo', rule: 'allowed' },
  tecnologia: { label: 'Tecnologia', rule: 'allowed' },
  telecom: { label: 'Telecomunicações', rule: 'allowed' },
  automotivo: { label: 'Automotivo', rule: 'allowed' },
  construcao: { label: 'Construção e imobiliário', rule: 'allowed' },
  saude_bem_estar: { label: 'Saúde e bem-estar (exceto medicamentos)', rule: 'allowed' },
  educacao: { label: 'Educação', rule: 'allowed' },
  moda_esporte: { label: 'Moda e artigos esportivos', rule: 'allowed' },
  servicos: { label: 'Serviços', rule: 'allowed' },
  industria: { label: 'Indústria', rule: 'allowed' },
  agronegocio: { label: 'Agronegócio', rule: 'allowed' },
  energia: { label: 'Energia', rule: 'allowed' },
  logistica: { label: 'Logística e transporte', rule: 'allowed' },
  midia: { label: 'Mídia e comunicação', rule: 'allowed' },
  governo: { label: 'Governo e órgãos públicos', rule: 'allowed' },
  outro: { label: 'Outro', rule: 'allowed' },
} as const satisfies Record<string, SponsorSectorDefinition>;

export type SponsorSector = keyof typeof SPONSOR_SECTORS;

export const SPONSOR_SECTOR_IDS = Object.keys(SPONSOR_SECTORS) as SponsorSector[];

export function isValidSponsorSector(value: unknown): value is SponsorSector {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SPONSOR_SECTORS, value);
}

export function getSponsorSectorDefinition(sector: unknown): SponsorSectorDefinition | null {
  if (!isValidSponsorSector(sector)) return null;
  return SPONSOR_SECTORS[sector] as SponsorSectorDefinition;
}

export function getSponsorSectorRule(sector: unknown): SponsorSectorRule | null {
  return getSponsorSectorDefinition(sector)?.rule ?? null;
}

/** Dados mínimos do alvo (seeker/asset) para decidir se o patrocínio é permitido */
export interface SponsorTarget {
  /** Seeker menor de idade */
  isMinor?: boolean;
  /** Evento/ativo com participação ou público de menores */
  involvesMinors?: boolean;
}

export interface SponsorTargetDecision {
  allowed: boolean;
  reason?: string;
}

/**
 * Decide se um patrocinador de determinado setor pode fazer oferta ao alvo.
 * Setor desconhecido é tratado como não permitido (falha fechada).
 */
export function canSponsorTarget(sector: unknown, seeker: SponsorTarget): SponsorTargetDecision {
  const rule = getSponsorSectorRule(sector);
  if (rule === null) {
    return { allowed: false, reason: 'Setor do patrocinador não informado ou inválido. Atualize o cadastro da empresa.' };
  }
  if (rule === 'blocked') {
    return { allowed: false, reason: getSponsorSectorDefinition(sector)?.message };
  }
  if (rule === 'adult_only' && (seeker.isMinor === true || seeker.involvesMinors === true)) {
    return {
      allowed: false,
      reason: 'Patrocinadores de bebidas alcoólicas não podem patrocinar menores de idade nem eventos com menores.',
    };
  }
  return { allowed: true };
}

// ============================================================================
// CENTRAL DE OCORRÊNCIAS (Triagem — seção 5.1)
// ============================================================================

export const OCORRENCIA_TIPOS = ['erro', 'feedback', 'bug', 'sugestao', 'processo_travado', 'avaliacao'] as const;
export type OcorrenciaTipo = typeof OCORRENCIA_TIPOS[number];

export const OCORRENCIA_STATUS = ['nova', 'em_analise', 'corrigido', 'deployado', 'verificado'] as const;
export type OcorrenciaStatus = typeof OCORRENCIA_STATUS[number];

export const FEEDBACK_TIPOS = ['feedback', 'bug', 'sugestao'] as const;
export type FeedbackTipo = typeof FEEDBACK_TIPOS[number];

/** Tipos aceitos em POST /feedback (inclui a micro-pesquisa 'avaliacao') */
export const FEEDBACK_ENVIO_TIPOS = [...FEEDBACK_TIPOS, 'avaliacao'] as const;
export type FeedbackEnvioTipo = typeof FEEDBACK_ENVIO_TIPOS[number];

/** Momentos-chave que disparam a micro-pesquisa de satisfação */
export const AVALIACAO_EVENTOS = [
  'ativo_publicado',
  'proposta_enviada',
  'candidatura_enviada',
  'proposta_respondida',
  'acordo_registrado',
  'kyc_enviado',
  'cadastro_concluido',
  /** Pesquisa geral (NPS 0–10), no máximo a cada AVALIACAO_GERAL_JANELA_DIAS */
  'geral',
] as const;
export type AvaliacaoEvento = typeof AVALIACAO_EVENTOS[number];

export const AVALIACAO_EVENTO_LABELS: Record<AvaliacaoEvento, string> = {
  ativo_publicado: 'Ativo publicado',
  proposta_enviada: 'Proposta enviada',
  candidatura_enviada: 'Candidatura enviada',
  proposta_respondida: 'Proposta respondida',
  acordo_registrado: 'Acordo fechado',
  kyc_enviado: 'Verificação (KYC) enviada',
  cadastro_concluido: 'Cadastro concluído',
  geral: 'Pesquisa geral (NPS)',
};

/** Eventos perguntados só uma vez na vida do usuário (primeiro ativo, primeira proposta) */
export const AVALIACAO_EVENTOS_SO_PRIMEIRO: readonly AvaliacaoEvento[] = ['ativo_publicado', 'proposta_enviada', 'candidatura_enviada'];

/** Pesquisa pós-ação: no máximo uma a cada 7 dias por usuário */
export const AVALIACAO_POS_ACAO_INTERVALO_DIAS = 7;
/** Pesquisa geral (NPS): no máximo a cada 90 dias */
export const AVALIACAO_GERAL_JANELA_DIAS = 90;
/** Conta precisa ter pelo menos 14 dias para receber a pesquisa geral */
export const AVALIACAO_GERAL_IDADE_MINIMA_DIAS = 14;

/** Faixa da nota por evento: pós-ação 1–5, geral (NPS) 0–10 */
export function faixaNotaAvaliacao(evento: AvaliacaoEvento): { min: number; max: number } {
  return evento === 'geral' ? { min: 0, max: 10 } : { min: 1, max: 5 };
}

/** Janela em que a mesma avaliação (usuário + evento) não é repetida */
export const AVALIACAO_JANELA_DIAS = 30;

export interface OcorrenciaAvaliacao {
  /** 1 a 5 (pós-ação) ou 0 a 10 (geral) */
  nota: number;
  evento: AvaliacaoEvento;
}

export interface OcorrenciaContexto {
  rota?: string;
  release?: string;
  userId?: string;
  objectId?: string;
  /** correlationId da resposta da API que falhou (relato a partir de um erro) */
  correlationId?: string;
  /** id da ocorrência de erro do frontend já enviada (ErrorBoundary) */
  erroId?: string;
  /** Dispositivo sem fingerprinting: só categorias e viewport */
  dispositivo?: FeedbackDispositivo;
  /** Últimos erros da sessão (resumo, sem payloads) */
  ultimosErros?: FeedbackErroRecente[];
}

export const DISPOSITIVO_TIPOS = ['desktop', 'tablet', 'mobile'] as const;
export type DispositivoTipo = typeof DISPOSITIVO_TIPOS[number];

export interface FeedbackDispositivo {
  tipo: DispositivoTipo;
  /** Ex.: "Android", "iOS", "Windows" */
  so?: string;
  /** Ex.: "Chrome 128" */
  navegador?: string;
  /** "LARGURAxALTURA" em px CSS */
  viewport?: string;
}

export interface FeedbackErroRecente {
  /** Mensagem curta (≤ 160) */
  mensagem: string;
  correlationId?: string;
  /** Origem: 'api' (resposta HTTP) ou 'cliente' (exceção JS) */
  origem: 'api' | 'cliente';
  /** ISO 8601 UTC */
  em: string;
}

export const FEEDBACK_MAX_ERROS_RECENTES = 5;
/** Captura de tela: só PNG/JPEG/WEBP, até 2MB */
export const FEEDBACK_CAPTURA_MAX_BYTES = 2 * 1024 * 1024;

/** Resposta de POST /feedback para feedback/bug/sugestão */
export interface FeedbackEnvioResposta {
  id: string;
  /** Número de acompanhamento legível, ex.: "FB-7K3Q2" */
  codigo?: string;
  /** Token de uso único (15 min) para anexar a captura de tela */
  capturaToken?: string;
  message: string;
}

export interface OcorrenciaHistoricoItem {
  status: OcorrenciaStatus;
  nota?: string;
  /** uid de quem alterou ou 'sistema' */
  por: string;
  /** ISO 8601 UTC */
  em: string;
}

export interface Ocorrencia {
  id: string;
  tipo: OcorrenciaTipo;
  status: OcorrenciaStatus;
  titulo: string;
  detalhes: string;
  fingerprint?: string;
  contagem: number;
  /** ISO 8601 UTC */
  primeiraVez: string;
  /** ISO 8601 UTC */
  ultimaVez: string;
  contexto: OcorrenciaContexto;
  historico: OcorrenciaHistoricoItem[];
  /** Autor (uid) quando o envio foi autenticado — recebe o retorno do time */
  autorId?: string;
  /** Presente apenas em ocorrências do tipo 'avaliacao' */
  avaliacao?: OcorrenciaAvaliacao;
  /** Resposta do time visível ao autor (separada da nota interna do histórico) */
  respostaPublica?: string;
  /** ISO 8601 UTC */
  respostaPublicaEm?: string;
  /** Número de acompanhamento (FB-XXXXX) */
  codigo?: string;
  /**
   * Captura de tela privada no Storage (feedback/{id}/{uuid}.png). Retenção:
   * apagada junto com a ocorrência; a Triagem deve removê-la após 'verificado'
   * ou em até 180 dias (ver docs/ESCOPO-LANCAMENTO.md 5.2).
   */
  capturaPath?: string;
  /** Hash SHA-256 do token de uso único para enviar a captura (nunca exposto) */
  capturaTokenHash?: string;
  /** ISO 8601 UTC — validade do token de captura */
  capturaTokenExpiraEm?: string;
  /** ISO 8601 UTC — autor viu o aviso "Você sugeriu, nós fizemos" */
  resolvidoVistoEm?: string;
}

/** Status que contam como "virou melhoria" */
export const OCORRENCIA_STATUS_RESOLVIDOS: readonly OcorrenciaStatus[] = ['corrigido', 'deployado', 'verificado'];

/** Item de GET /feedback/mine — só o que o autor pode ver (sem notas internas) */
export interface MeuFeedbackItem {
  id: string;
  tipo: FeedbackTipo;
  texto: string;
  status: OcorrenciaStatus;
  /** ISO 8601 UTC */
  criadoEm: string;
  linhaDoTempo: { status: OcorrenciaStatus; em: string }[];
  respostaPublica?: string;
  respostaPublicaEm?: string;
  codigo?: string;
  /** Autor já viu o aviso de resolvido */
  resolvidoVisto: boolean;
}

// ============================================================================
// NOVIDADES (seção 5.2 — "Você sugeriu, nós fizemos")
// ============================================================================

export const NOVIDADE_TIPOS = ['melhoria', 'correcao', 'novo'] as const;
export type NovidadeTipo = typeof NOVIDADE_TIPOS[number];

export const NOVIDADE_TIPO_LABELS: Record<NovidadeTipo, string> = {
  melhoria: 'Melhoria',
  correcao: 'Correção',
  novo: 'Novo recurso',
};

export interface Novidade {
  id: string;
  titulo: string;
  descricao: string;
  /** ISO 8601 UTC */
  publicadaEm: string;
  /** Ocorrências que originaram a novidade (uso interno; não expostas no GET público) */
  ocorrenciaIds: string[];
  vindaDeUsuarios: boolean;
  tipo: NovidadeTipo;
}

/** Item público de GET /novidades (sem ocorrenciaIds) */
export type NovidadePublica = Omit<Novidade, 'ocorrenciaIds'>;

/** GET /admin/feedback/metricas */
export interface FeedbackMetricas {
  dias: number;
  /** ISO 8601 UTC */
  desde: string;
  usuariosAtivos: number;
  autoresDeFeedback: number;
  /** autoresDeFeedback / usuariosAtivos (0–1); null sem usuários ativos */
  taxaFeedbackPorAtivo: number | null;
  totalFeedbacks: number;
  viraramMelhoria: number;
  nps: { score: number | null; respostas: number; promotores: number; neutros: number; detratores: number };
  mediaPorEvento: AvaliacaoResumoItem[];
  truncado: boolean;
}

export interface AvaliacaoResumoItem {
  evento: AvaliacaoEvento;
  total: number;
  /** Média das notas (1–5) */
  media: number;
  comComentario: number;
}

export interface FunilSnapshot {
  /** YYYY-MM-DD (UTC) */
  id: string;
  kycPendenteMais3Dias: number;
  ofertasSemRespostaMais7Dias: number;
  negociacoesSemMensagemMais14Dias: number;
  assetsSemOfertaMais30Dias: number;
  /** ISO 8601 UTC */
  criadoEm: string;
}
